/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useRef } from 'react'
import { AutoComplete, Switch } from 'antd'
import { GlobalOutlined, SearchOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getTeamSearchCardState } from '@sportninja/common/features/competition/operations/utils/teamSearchCardState'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import type { OrgSearchTeam } from '@sportninja/common/services/team/orgTeamSearchService'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import PersonSearchIcon from 'src/components/ts-components/icons/PersonSearchIcon'

const translate = t as (key: string, options?: unknown) => string

// Matches the roster Add Player modal — Figma secondary-50, absent from
// appColors. SECONDARY_150 / SECONDARY_350 are likewise missing from the typed
// inference and read through a cast, the established pattern.
const SEARCH_FIELD_BORDER = '#48556a'
const palette = colors as Record<string, string>

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface TeamSearchStepProps {
  query: string
  onQueryChange: (value: string) => void
  isGlobal: boolean
  onGlobalChange: (value: boolean) => void
  teams: OrgSearchTeam[]
  isFetching: boolean
  onSelectTeam: (team: OrgSearchTeam) => void
  isNewTeamMode: boolean
  newTeamName: string
  onNewTeamNameChange: (value: string) => void
  /** Teams already in this division — dropped from the results. */
  existingTeamIds: Set<string>
}

/** One result row: crest, name, id, and the owning organization. */
const TeamOption = ({ team }: { team: OrgSearchTeam }) => (
  <div css={optionStyle}>
    <div css={optionTopRowStyle}>
      <img
        src={resolveAssetUrl(team.imageUrl) || TEAM_PLACEHOLDER}
        alt=''
        css={optionAvatarStyle}
        onError={(event) => {
          const img = event.target as HTMLImageElement
          img.onerror = null
          img.src = TEAM_PLACEHOLDER
        }}
      />
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {team.nameFull}
      </Text>
      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_50}
      >
        {` (${team.id})`}
      </Text>
    </div>
    {team.organizationId ? (
      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_100}
      >
        {`Organization ID: ${team.organizationId}`}
      </Text>
    ) : null}
  </div>
)

/**
 * Step one: pick an existing team, or name a new one.
 *
 * Styled after the roster Add Player modal — results live in the AutoComplete
 * dropdown and picking one IS the action, so there is no separate Select
 * button. Teams already in the division are filtered out rather than badged;
 * an unselectable row in a dropdown reads as a dead end.
 */
const TeamSearchStep = ({
  query,
  onQueryChange,
  isGlobal,
  onGlobalChange,
  teams,
  isFetching,
  onSelectTeam,
  isNewTeamMode,
  newTeamName,
  onNewTeamNameChange,
  existingTeamIds,
}: TeamSearchStepProps) => {
  'use memo'

  const selectRef = useRef<{ blur?: () => void } | null>(null)

  const available = teams.filter((team) => !existingTeamIds.has(team.id))

  const options = available.map((team) => ({
    value: team.id,
    label: <TeamOption team={team} />,
  }))

  const cardState = getTeamSearchCardState({
    isSearching: isFetching,
    resultCount: options.length,
  })

  if (isNewTeamMode) {
    return (
      <div css={bodyStyle}>
        <TextInput
          name='teamName'
          value={newTeamName}
          setValue={onNewTeamNameChange}
          label={`${capitalizeWords(translate('common:name'))} *`}
          placeholder='e.g. The Rockets, NSWC Bantam A1, etc.'
          // Both are required and indexed into unguarded (`fieldErrors[name]`),
          // so they must be objects — `undefined` throws on render.
          fieldErrors={{}}
          customError={{}}
        />
      </div>
    )
  }

  return (
    <div css={bodyStyle}>
      <div css={searchFieldStyle}>
        <div css={labelRowStyle}>
          {/* Uppercased in CSS, not JS — `t()` can return undefined before
              i18n settles, and `.toUpperCase()` on that throws. */}
          <Text
            variant='captionSmall'
            color={colors.WHITE}
            weight='semiBold'
            style='text-transform: uppercase;'
          >
            {translate('common:team')}
          </Text>
          <button
            type='button'
            css={globalSearchButtonStyle}
            onClick={() => onGlobalChange(!isGlobal)}
          >
            <GlobalOutlined css={globeIconStyle} />
            <Text variant='paragraphXSmall' color={colors.WHITE}>
              {translate('Web:searchRootOrganization')}
            </Text>
            <Switch size='small' checked={isGlobal} />
          </button>
        </div>

        <div css={searchInputWrapperStyle}>
          <SearchOutlined css={searchIconStyle} />
          <AutoComplete
            ref={selectRef as never}
            css={autoCompleteStyle}
            placeholder={capitalizeWords(translate('Web:searchForATeam'))}
            value={query}
            onSearch={onQueryChange}
            onSelect={(teamId: string) => {
              selectRef.current?.blur?.()
              const picked = available.find((team) => team.id === teamId)
              if (picked) {
                onSelectTeam(picked)
              }
            }}
            notFoundContent={null}
            options={options}
            allowClear
            onClear={() => onQueryChange('')}
            // The popup portals to document.body at antd's default z-index
            // (~1050), which is BELOW BaseModal's wrapper — without this the
            // results render behind the dialog.
            dropdownStyle={{ zIndex: MODAL_TOKENS.Z_INDEX_WRAPPER + 1 }}
          />
        </div>
      </div>

      {/* Always present; results render in the dropdown above the field, so
          'hidden' falls back to the idle copy rather than collapsing the box. */}
      <div css={stateCardStyle}>
        <PersonSearchIcon size={40} color={colors.NEUTRAL_50} />
        {cardState === 'searching' ? (
          <Text variant='paragraphMedium' weight='medium' color={colors.WHITE}>
            {`${translate('common:searching')}...`}
          </Text>
        ) : (
          <div css={stateCardTextStyle}>
            <Text
              variant='paragraphMedium'
              weight='medium'
              color={colors.WHITE}
            >
              {cardState === 'empty'
                ? translate('Web:noTeamsFound')
                : translate('Web:findATeam')}
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
              style='text-align: center;'
            >
              {cardState === 'empty'
                ? translate('Web:noTeamsFoundHint')
                : translate('Web:findATeamHint')}
            </Text>
          </div>
        )}
      </div>
    </div>
  )
}

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const searchFieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

const labelRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const globalSearchButtonStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
`

const globeIconStyle = css`
  color: ${colors.WHITE};
  font-size: 16px;
`

const searchInputWrapperStyle = css`
  position: relative;
  width: 100%;
`

const searchIconStyle = css`
  position: absolute;
  left: 16px;
  top: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  line-height: 0;
  color: ${colors.NEUTRAL_50};
  font-size: 18px;
  pointer-events: none;
  /* antd wraps the glyph in a <span>; flatten its line-box so the svg centers. */
  svg {
    display: block;
  }
`

const autoCompleteStyle = css`
  width: 100%;
  /* Force the select ROOT to the field height so the wrapper — and the
     absolutely positioned search icon anchored to it — is exactly 50px. */
  &.ant-select {
    height: 50px !important;
  }
  .ant-select-selector {
    background: ${palette.SECONDARY_150} !important;
    border: 1px solid ${SEARCH_FIELD_BORDER} !important;
    border-radius: 8px !important;
    height: 50px !important;
    padding-left: 44px !important;
  }
  .ant-select-selection-search {
    display: flex;
    align-items: center;
    inset-inline-start: 44px !important;
  }
  .ant-select-selection-search-input {
    height: 48px !important;
    font-size: 14px !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-selection-placeholder {
    line-height: 48px !important;
    inset-inline-start: 44px !important;
    color: ${colors.NEUTRAL_50} !important;
  }
  .ant-select-clear {
    position: absolute;
    right: 12px;
    top: 24px;
  }
`

const stateCardStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
`

const stateCardTextStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

const optionStyle = css`
  align-self: center;
  margin-top: 2px;
`

const optionTopRowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`

const optionAvatarStyle = css`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: contain;
  margin-right: 4px;
`

export default TeamSearchStep
